import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { ErrorBoundary } from "./ErrorBoundary";
import { TrashPanel } from "./TrashPanel";
import MaintenanceBanner from "./MaintenanceBanner";
import { suspendNavDrafts } from "../lib/navDraft";
import {
  LayoutDashboard, FileSpreadsheet, Receipt, Handshake, BarChart3,
  Building2, Wrench, Users, UserCog, LogOut, Database, ListTree, Landmark, Wallet, Armchair, Package, ShieldCheck
} from "lucide-react";
import { ClipboardCheck, HardHat, Menu, CalendarOff, CalendarDays, Megaphone, CreditCard, Calculator, BadgeDollarSign } from "lucide-react";
import { http, formatApiError } from "../lib/api";
import { canSee, ROLE_LABEL, LEDGER_ROLES, PAYROLL_ROLES, FIN_VIEW_ROLES } from "../lib/roles";
import { toast } from "../lib/notify";

const navSections = [
  {
    title: "Genel Bakış",
    items: [{ to: "/dashboard", label: "Dashboard & Özet", icon: LayoutDashboard, testid: "nav-dashboard" }],
  },
  {
    title: "Operasyon",
    items: [
      { to: "/veri-girisi", label: "Veri Girişi", icon: FileSpreadsheet, testid: "nav-data-entry" },
      { to: "/izin-takvimi", label: "İzin Takvimi", icon: CalendarDays, testid: "nav-leave-calendar" },
      { to: "/onaylar", label: "Hizmet Onayları", icon: ShieldCheck, testid: "nav-approvals" },
      { to: "/is-emirleri", label: "İş Emirleri", icon: ClipboardCheck, testid: "nav-work-orders" },
      { to: "/musteri-hakedis", label: "Müşteri Hakedişi", icon: Receipt, testid: "nav-customer-claim" },
      { to: "/taseron-hakedis", label: "Taşeron Hakedişi", icon: Handshake, testid: "nav-subcontractor-claim" },
      { to: "/satis-pazarlama", label: "Satış Pazarlama", icon: Megaphone, testid: "nav-sales" },
      { to: "/muhasebe", label: "Muhasebe", icon: Calculator, testid: "nav-muhasebe", roles: LEDGER_ROLES },
      { to: "/bordro", label: "Bordro", icon: BadgeDollarSign, testid: "nav-bordro", roles: PAYROLL_ROLES },
    ],
  },
  {
    title: "Fatura & Bütçe",
    items: [
      { to: "/odeme-plani", label: "Ödeme Planı", icon: Handshake, testid: "nav-payment-plan" },
      { to: "/butce", label: "Bütçe", icon: BarChart3, testid: "nav-budget" },
      { to: "/proje-maliyet", label: "Proje Maliyet Özeti", icon: BarChart3, testid: "nav-project-cost" },
      { to: "/kesilen-faturalar", label: "Kesilen Faturalar", icon: Receipt, testid: "nav-issued-invoices" },
      { to: "/demirbaslar", label: "Demirbaşlar", icon: Armchair, testid: "nav-demirbaslar" },
      { to: "/stoklar", label: "Stoklar", icon: Package, testid: "nav-stoklar" },
      { to: "/mali-veriler", label: "Mali Veriler", icon: Landmark, testid: "nav-mali-veriler", roles: FIN_VIEW_ROLES },
    ],
  },
  {
    title: "İnsan Kaynakları",
    roles: ["ik"],
    items: [{ to: "/tanimlar/tatiller", label: "Resmi Tatiller", icon: CalendarOff, testid: "nav-ik-holidays" }],
  },
  {
    title: "Yönetici Tanımları",
    adminOnly: true,
    items: [
      { to: "/tanimlar/projeler", label: "Projeler", icon: Building2, testid: "nav-def-projects" },
      { to: "/tanimlar/hizmetler", label: "Hizmetler", icon: Wrench, testid: "nav-def-services" },
      { to: "/tanimlar/butce-kalemleri", label: "Bütçe Kalemleri", icon: Wallet, testid: "nav-def-budget-items" },
      { to: "/tanimlar/sabit-tanimlar", label: "Sabit Tanımlar", icon: ListTree, testid: "nav-def-sabit-tanimlar" },
      { to: "/tanimlar/kullanicilar", label: "Kullanıcılar", icon: UserCog, testid: "nav-def-users" },
      { to: "/tanimlar/yetki-matrisi", label: "Rol Yetki Matrisi", icon: ShieldCheck, testid: "nav-def-role-matrix" },
      { to: "/tanimlar/yedek", label: "Yedek Al", icon: Database, testid: "nav-def-backup" },
      { to: "/cariler", label: "Cari Listesi", icon: Users, testid: "nav-caris" },
      { to: "/taseronlar", label: "Taşeronlar", icon: HardHat, testid: "nav-subcontractors" },
    ],
  },
];

function NavBadge({ item, pendingAppr, missingCost }) {
  if (item.to === "/onaylar" && pendingAppr > 0) {
    return <span className="ml-auto px-1.5 py-0.5 rounded-full bg-amber-500 text-slate-900 text-[10px] font-bold leading-none" title={`${pendingAppr} kayıt onay bekliyor`} data-testid="nav-approvals-pending-badge">{pendingAppr}</span>;
  }
  if (item.to === "/proje-maliyet" && missingCost.count > 0) {
    return <span className="ml-auto px-1.5 py-0.5 rounded-full bg-red-500 text-white text-[10px] font-bold leading-none" title={`${missingCost.period} döneminde maliyeti yapılmayan ${missingCost.count} proje`} data-testid="nav-project-cost-missing-badge">{missingCost.count}</span>;
  }
  return null;
}

function SidebarNav({ user, isAdmin, pendingAppr, missingCost, roleSetsVersion }) {
  const sections = useMemo(() => navSections
    .filter((s) => !(s.adminOnly && !isAdmin) && !(s.roles && !s.roles.includes(user?.role)))
    .map((s) => ({ ...s, items: s.items.filter((it) => canSee(user, it.to) && (!it.roles || isAdmin || it.roles.includes(user?.role))) }))
    .filter((s) => s.items.length > 0), [user, isAdmin, roleSetsVersion]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-6">
      {sections.map((s) => (
        <div key={s.title}>
          <div className="px-3 mb-2 text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{s.title}</div>
          <div className="space-y-1">
            {s.items.map((it) => (
              <NavLink key={it.to} to={it.to} data-testid={it.testid} className={({ isActive }) => `sidebar-item ${isActive ? "active" : ""}`}>
                <it.icon className="w-4 h-4" />
                <span data-no-upper>{it.label}</span>
                <NavBadge item={it} pendingAppr={pendingAppr} missingCost={missingCost} />
              </NavLink>
            ))}
          </div>
        </div>
      ))}
    </nav>
  );
}

function SidebarFooter({ user, isAdmin, onSeed, onLogout }) {
  const btn = "w-full flex items-center gap-2 text-xs text-slate-300 hover:text-white px-3 py-2 rounded-md hover:bg-slate-800";
  return (
    <div className="px-3 py-4 border-t border-slate-800 space-y-2">
      {isAdmin && <button onClick={onSeed} data-testid="btn-seed-demo" className={btn}><Database className="w-4 h-4" /> Demo verisini yükle</button>}
      {isAdmin && <TrashPanel buttonClassName={btn} />}
      <div className="px-3 py-2 text-xs text-slate-400">
        <div className="font-semibold text-slate-200 truncate" data-testid="user-name">{user?.name}</div>
        <div className="truncate" data-no-upper>{user?.email}</div>
        <div className="mt-1 inline-flex px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 text-[10px] uppercase tracking-widest" data-testid="user-role-badge">
          {isAdmin ? "Yönetici" : ROLE_LABEL[user?.role] || "Veri Giriş"}
        </div>
      </div>
      <button onClick={onLogout} data-testid="btn-logout" className={btn}><LogOut className="w-4 h-4" /> Çıkış Yap</button>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const loc = useLocation();
  useEffect(() => { if (user?.must_change_password && loc.pathname !== "/sifre-degistir") navigate("/sifre-degistir"); }, [user, loc.pathname, navigate]);
  const [missingCost, setMissingCost] = useState({ count: 0, period: null });
  const [pendingAppr, setPendingAppr] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  useEffect(() => { setMenuOpen(false); }, [loc.pathname]);
  const refreshPending = useCallback(() => { if (canSee(user, "/onaylar")) http.get("/entries/approvals/pending-count").then((r) => setPendingAppr(r.data?.pending || 0)).catch(() => {}); }, [user]);
  useEffect(() => {
    http.get("/project-costs/missing-projects").then((r) => setMissingCost(r.data || { count: 0 })).catch(() => {});
    refreshPending();
  }, [loc.pathname, refreshPending]);
  useEffect(() => {
    window.addEventListener("approvals-changed", refreshPending);
    return () => window.removeEventListener("approvals-changed", refreshPending);
  }, [refreshPending]);

  const doSeed = async () => {
    const typed = window.prompt("DİKKAT: Demo verisi tüm proje, şube, hizmet, cari ve hizmet kayıtlarını SİLER ve örnek veriyle değiştirir.\nDevam etmek için SIFIRLA yazın:");
    if (typed !== "SIFIRLA") return;
    try {
      await http.post("/demo/seed", null, { params: { confirm: "SIFIRLA" } });
      toast.success("Demo verisi yüklendi. Sayfayı yenileyin.");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doLogout = async () => {
    suspendNavDrafts();   // çıkışta açık form taslağı yazılmasın
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {menuOpen && <div className="fixed inset-0 bg-black/50 z-30 md:hidden" onClick={() => setMenuOpen(false)} data-testid="sidebar-backdrop" />}
      <aside className={`w-64 shrink-0 bg-slate-900 text-slate-100 flex flex-col fixed inset-y-0 left-0 z-40 transition-transform md:static md:translate-x-0 ${menuOpen ? "translate-x-0" : "-translate-x-full"}`} data-testid="sidebar">
        <div className="px-5 py-5 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="rounded-md bg-white px-2 py-1.5 shadow" data-testid="sidebar-logo">
              <img src="/wilco-logo.png" alt="WILCO" className="h-9 w-auto" />
            </div>
            <div>
              <div className="text-sm font-semibold tracking-wide">WILCO_PAS</div>
              <div className="text-[10px] uppercase tracking-widest text-slate-400">Demo v0.1</div>
            </div>
          </div>
        </div>
        <SidebarNav user={user} isAdmin={isAdmin} pendingAppr={pendingAppr} missingCost={missingCost} />
        <SidebarFooter user={user} isAdmin={isAdmin} onSeed={doSeed} onLogout={doLogout} />
      </aside>

      <main className="flex-1 min-w-0 overflow-y-auto">
        <div className="md:hidden sticky top-0 z-20 flex items-center gap-3 px-4 py-3 bg-slate-900 text-white" data-testid="mobile-topbar">
          <button type="button" onClick={() => setMenuOpen(true)} data-testid="mobile-menu-btn" className="p-1.5 rounded-md hover:bg-slate-800" aria-label="Menü"><Menu className="w-5 h-5" /></button>
          <img src="/wilco-logo.png" alt="WILCO" className="h-6 w-auto rounded bg-white px-1" />
          <span className="font-bold tracking-tight">WILCO_PAS</span>
        </div>
        <MaintenanceBanner />
        <div className="app-content max-w-7xl mx-auto px-4 md:px-6 lg:px-10 py-5 md:py-8 space-y-6 md:space-y-8"><ErrorBoundary resetKey={loc.pathname + loc.search}>{children}</ErrorBoundary></div>
      </main>
    </div>
  );
}
